import PropTypes from "prop-types";
import { useState } from "react";
import { Ban } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";
import LeadStatusBadge from "routes/leads/swoogo_registrants/LeadStatusBadge";
import RegistrantLeadStatusModal from "routes/leads/swoogo_registrants/RegistrantLeadStatusModal";
import useCanSetRegistrantLeadStatus from "routes/leads/swoogo_registrants/useCanSetRegistrantLeadStatus";
import useSwoogoRegistrantLeads from "routes/leads/swoogo_registrants/useSwoogoRegistrantLeads";
import useSwoogoEventDatasets from "routes/meta_datasets/swoogo_events/useSwoogoEventDatasets";

// Shows the registrant's CRM lead status and, because the registrant carries an
// fbclid, lets a user set it. Setting the status creates the CRM lead on first
// use and reports it to Meta's Conversions API.
const RegistrantLeadStatusCard = ({ registrant }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const canSetStatus = useCanSetRegistrantLeadStatus();
  const canReadDatasets = useAuthorized([
    ACL_RELATIONSHIPS.metaAdAccount.readSwoogoEventDatasets,
  ]);

  const { leadsByRegistrant } = useSwoogoRegistrantLeads([registrant.id]);
  const { links } = useSwoogoEventDatasets([registrant.event_id], {
    enabled: canReadDatasets,
  });

  const row = leadsByRegistrant.get(registrant.id);
  if (!row?.eligible) {
    return null;
  }
  const status = row.lead?.status ?? null;
  const isDatasetUnavailable = !!links.get(registrant.event_id)?.unavailable;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meta Status</CardTitle>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-3">
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-4">
          {status ? (
            <LeadStatusBadge status={status} />
          ) : (
            <span className="tw:text-muted-foreground">No status set</span>
          )}
          {canSetStatus && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsModalOpen(true)}
            >
              {status ? "Change Status" : "Set Status"}
            </Button>
          )}
        </div>
        {isDatasetUnavailable && (
          <p className="tw:m-0 tw:flex tw:items-center tw:gap-1.5 tw:text-muted-foreground">
            <Ban className="tw:size-4 tw:shrink-0" />
            The event&apos;s Meta dataset was removed. Nothing will be reported
            to Meta.
          </p>
        )}
      </CardContent>
      {canSetStatus && (
        <RegistrantLeadStatusModal
          registrantIds={[registrant.id]}
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
        />
      )}
    </Card>
  );
};

RegistrantLeadStatusCard.propTypes = {
  registrant: PropTypes.shape({
    id: PropTypes.number.isRequired,
    event_id: PropTypes.number.isRequired,
  }).isRequired,
};

export default RegistrantLeadStatusCard;
